/**
 * LegalTemplates PDF Editor — page pipeline, zoom, thumbnails, scrolling
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { activateTool } from './ui.js';
import { countNativeFormFields } from './form-fill.js';
import { pdfjsLib } from './context.js';
import { redrawAllPages, redrawPage, updateUndoRedoButtons } from './annotations.js';
import {
    DRAG_TOOLS,
    THUMB_WIDTH,
    VIRTUALIZE_MIN_PAGES,
    ZOOM_MAX,
    ZOOM_MAX_BACKING_WIDTH,
    ZOOM_STEPS,
    canvasAreaEl,
    colorInput,
    loadingEl,
    pageInputEl,
    pageTotalEl,
    pagesEl,
    state,
    thumbsEl,
    toolDefaultColors,
    zoomInButton,
    zoomLevelButton,
    zoomOutButton,
    zoomPillEl,
} from './context.js';
import { clamp, hasLoadedDocumentForActions, runBackgroundPreparation } from './core.js';
import { applyDraftToState, computeDocumentKey, offerDraftRestore } from './drafts.js';
import {
    addFormFieldsToLoadedValuesMap,
    commitInlineFormFieldEditor,
    extractPageFormFields,
    isFormFieldOverrideEqualToLoaded,
    updateFormFillSummary,
} from './form-fill.js';
import { ensureBasePdfBytesValid, hideUploadEmptyState } from './io.js';
import { syncDeletePagesSummary, syncSplitExtractSummary, updateMergeInsertOptions } from './modals.js';
import { createPageShell, getPageShells, isShellNearViewport } from './shell.js';
import { enrichTextFragmentsFromCanvas, extractPageTextFragments } from './text.js';
import { refreshPageCursor, setStatus, syncDocumentRequiredActionsDisabledState } from './ui.js';

export function renderEmptyThumbPlaceholder() {
    if (!thumbsEl) {
        return;
    }

    thumbsEl.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'pdf-thumb-item pdf-thumb-empty-placeholder';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.innerHTML = `
        <div class="pdf-thumb-placeholder-canvas">
            <div class="pdf-thumb-placeholder-sheet"></div>
        </div>
        <p class="pdf-thumb-label">Page 1</p>
    `;
    thumbsEl.appendChild(wrapper);
}

export function getSplitExtractPreviewThumbForPage(pageNumber) {
    const normalizedPage = Math.max(1, Number(pageNumber || 1));
    const cached = state.pageThumbPreviewUrls.get(normalizedPage);
    if (cached) {
        return cached;
    }

    const thumbCanvas = thumbsEl
        ? thumbsEl.querySelector(`.pdf-thumb-item[data-page="${normalizedPage}"] canvas`)
        : null;

    if (!thumbCanvas || typeof thumbCanvas.toDataURL !== 'function') {
        return '';
    }

    try {
        const dataUrl = thumbCanvas.toDataURL('image/png');
        if (dataUrl) {
            state.pageThumbPreviewUrls.set(normalizedPage, dataUrl);
        }
        return dataUrl || '';
    } catch (error) {
        return '';
    }
}

export function getZoom() {
    const zoom = Number(state.zoom);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

export function computeFitRenderWidth() {
    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
    const canvasWidth = Math.max(
        0,
        ((canvasAreaEl && canvasAreaEl.clientWidth) ? canvasAreaEl.clientWidth : pagesEl.clientWidth) - 20
    );
    const viewportWidth = Math.max(
        0,
        ((window.innerWidth || document.documentElement.clientWidth || 0) - 20)
    );
    const widthBudget = isMobileViewport
        ? Math.min(canvasWidth || viewportWidth, viewportWidth || canvasWidth)
        : (canvasWidth || viewportWidth);
    return Math.max(240, Math.min(widthBudget, 960));
}

export function getFitZoom() {
    const fitWidth = computeFitRenderWidth();
    const baseWidth = Number(state.baseRenderWidth) || fitWidth;
    return baseWidth > 0 ? fitWidth / baseWidth : 1;
}

export function getZoomMin() {
    // Fit-to-width may legitimately sit below 50% when the pages were
    // rendered for a wider window (restored drafts, rotation).
    return Math.min(0.5, getFitZoom());
}

export function getScrollContainer() {
    if (canvasAreaEl) {
        const overflowY = getComputedStyle(canvasAreaEl).overflowY;
        if (/(auto|scroll)/.test(overflowY) && canvasAreaEl.scrollHeight > canvasAreaEl.clientHeight + 1) {
            return canvasAreaEl;
        }
    }
    return document.scrollingElement || document.documentElement;
}

function clampZoom(value) {
    return clamp(Math.round(Number(value) * 1000) / 1000, getZoomMin(), ZOOM_MAX);
}

function resolveZoomAnchor(clientX, clientY) {
    if (clientX != null && clientY != null) {
        return { x: clientX, y: clientY };
    }
    const scroller = getScrollContainer();
    const scrollerIsWindow = scroller === document.scrollingElement || scroller === document.documentElement;
    const rect = scrollerIsWindow
        ? { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight }
        : scroller.getBoundingClientRect();
    return { x: rect.left + (rect.width / 2), y: rect.top + (rect.height / 2) };
}

// Sets the zoom and scrolls so that the document point (base units) sits
// under the given client anchor. Vertical scrolling goes to whichever element
// scrolls the pages; horizontal scrolling goes to the canvas area whenever it
// is the one overflowing (phones: window scrolls vertically, the area
// scrolls sideways).
function applyZoomAtDocPoint(zoom, docX, docY, anchorX, anchorY) {
    const scroller = getScrollContainer();
    const scrollerIsWindow = scroller === document.scrollingElement || scroller === document.documentElement;

    pagesEl.style.setProperty('--pdf-zoom', String(zoom));
    state.zoom = zoom;

    const afterRect = pagesEl.getBoundingClientRect();
    const dx = (afterRect.left + (docX * zoom)) - anchorX;
    const dy = (afterRect.top + (docY * zoom)) - anchorY;

    if (scrollerIsWindow) {
        window.scrollBy(0, dy);
    } else {
        scroller.scrollTop += dy;
    }

    const areaScrollsSideways = !!(canvasAreaEl && canvasAreaEl.scrollWidth > canvasAreaEl.clientWidth + 1);
    if (areaScrollsSideways) {
        canvasAreaEl.scrollLeft += dx;
    } else if (scrollerIsWindow) {
        window.scrollBy(dx, 0);
    } else {
        scroller.scrollLeft += dx;
    }
}

export function applyZoom(nextZoom, options = {}) {
    const { anchorClientX = null, anchorClientY = null, mode = 'manual' } = options;
    const zoom = clampZoom(nextZoom);
    const previous = getZoom();
    state.zoomMode = mode;

    if (Math.abs(zoom - previous) < 0.001) {
        syncZoomUi();
        return;
    }

    const anchor = resolveZoomAnchor(anchorClientX, anchorClientY);
    const beforeRect = pagesEl.getBoundingClientRect();
    // The document-space point (base units) currently under the anchor.
    const docX = (anchor.x - beforeRect.left) / previous;
    const docY = (anchor.y - beforeRect.top) / previous;

    applyZoomAtDocPoint(zoom, docX, docY, anchor.x, anchor.y);
    syncZoomUi();
    scheduleZoomRerender();
}

// ------------------------------------------------------------------
// Pinch to zoom (touch)
//
// The canvas area and the page shells allow one-finger panning natively
// (touch-action: pan-x pan-y) but never native pinch-zoom, so two fingers
// over the document zoom the document alone — the toolbars stay put. The
// point between the fingers at the start of the gesture stays under them,
// which gives pan and zoom in one motion. Crisp re-rendering waits until
// the fingers lift.
// ------------------------------------------------------------------
function touchDistance(a, b) {
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

function touchMidpoint(a, b) {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

function abandonDragInteraction() {
    if (!state.interaction) {
        return;
    }
    const pageNumber = state.interaction.page;
    state.interaction = null;
    state.draft = null;
    if (pageNumber) {
        redrawPage(pageNumber);
    }
}

export function bindPinchZoom() {
    if (!canvasAreaEl) {
        return;
    }

    const onTouchStart = (event) => {
        if (!state.pdfJsDoc || !event.touches || event.touches.length !== 2) {
            return;
        }
        const [a, b] = event.touches;
        const mid = touchMidpoint(a, b);
        const rect = pagesEl.getBoundingClientRect();
        const zoom = getZoom();
        state.pinch.active = true;
        state.pinch.startDistance = Math.max(1, touchDistance(a, b));
        state.pinch.startZoom = zoom;
        state.pinch.docX = (mid.x - rect.left) / zoom;
        state.pinch.docY = (mid.y - rect.top) / zoom;
        // A drag tool may have started drawing with the first finger.
        abandonDragInteraction();
        if (event.cancelable) {
            event.preventDefault();
        }
    };

    const onTouchMove = (event) => {
        if (!state.pinch.active || !event.touches || event.touches.length !== 2) {
            return;
        }
        if (event.cancelable) {
            event.preventDefault();
        }
        const [a, b] = event.touches;
        const mid = touchMidpoint(a, b);
        const scale = touchDistance(a, b) / state.pinch.startDistance;
        const zoom = clampZoom(state.pinch.startZoom * scale);
        state.zoomMode = 'manual';
        applyZoomAtDocPoint(zoom, state.pinch.docX, state.pinch.docY, mid.x, mid.y);
        syncZoomUi();
    };

    const onTouchEnd = (event) => {
        if (!state.pinch.active) {
            return;
        }
        if (event.touches && event.touches.length >= 2) {
            return;
        }
        state.pinch.active = false;
        scheduleZoomRerender();
        scheduleViewportWork();
    };

    canvasAreaEl.addEventListener('touchstart', onTouchStart, { passive: false });
    canvasAreaEl.addEventListener('touchmove', onTouchMove, { passive: false });
    canvasAreaEl.addEventListener('touchend', onTouchEnd, { passive: true });
    canvasAreaEl.addEventListener('touchcancel', onTouchEnd, { passive: true });
}

export function zoomStep(direction, anchor = {}) {
    const zoom = getZoom();
    let next;
    if (direction > 0) {
        next = ZOOM_STEPS.find((step) => step > zoom + 0.001);
        if (next === undefined) {
            next = ZOOM_MAX;
        }
    } else {
        next = ZOOM_STEPS.slice().reverse().find((step) => step < zoom - 0.001);
        if (next === undefined) {
            next = getZoomMin();
        }
    }
    applyZoom(next, { ...anchor, mode: 'manual' });
}

export function zoomToFit() {
    applyZoom(getFitZoom(), { mode: 'fit' });
}

export function syncZoomUi() {
    if (!zoomPillEl) {
        return;
    }
    const hasDocument = hasLoadedDocumentForActions();
    zoomPillEl.classList.toggle('hidden', !hasDocument);
    const zoom = getZoom();
    if (zoomLevelButton) {
        zoomLevelButton.textContent = `${Math.round(zoom * 100)}%`;
    }
    if (zoomOutButton) {
        zoomOutButton.disabled = zoom <= getZoomMin() + 0.001;
    }
    if (zoomInButton) {
        zoomInButton.disabled = zoom >= ZOOM_MAX - 0.001;
    }
}

export function scheduleZoomRerender() {
    window.clearTimeout(state.zoomRerenderTimer);
    state.zoomRerenderTimer = window.setTimeout(() => {
        rerenderPagesForZoom().catch(() => {});
    }, 260);
}

export function zoomRenderKey(zoom) {
    return String(Math.round(Math.max(1, zoom) * 100));
}

export async function rerenderPagesForZoom() {
    if (!state.pdfJsDoc || state.pinch.active) {
        return;
    }
    const zoom = getZoom();
    const generation = state.renderGeneration;
    // Pending pages get their base render from preparePage; released
    // pages come back through materializeNearPages.
    const shells = getPageShells().filter((shell) => shell.dataset.state === 'ready');
    const near = shells.filter((shell) => isShellNearViewport(shell));
    const far = shells.filter((shell) => !isShellNearViewport(shell));

    for (const shell of near) {
        if (generation !== state.renderGeneration) {
            return;
        }
        await rerenderPageCanvas(shell, zoom, generation);
    }

    // Pages that scrolled out of view give their high-resolution backing
    // store back so a long zoomed document does not pile up memory.
    for (const shell of far) {
        if (generation !== state.renderGeneration) {
            return;
        }
        if (shell.dataset.renderedZoom && shell.dataset.renderedZoom !== zoomRenderKey(1)) {
            await rerenderPageCanvas(shell, 1, generation);
        }
    }
}

export async function rerenderPageCanvas(pageShell, zoom, generation) {
    const targetKey = zoomRenderKey(zoom);
    if (pageShell.dataset.renderedZoom === targetKey) {
        return;
    }
    const pageNumber = Number(pageShell.dataset.page);
    const layout = state.pageLayouts.get(pageNumber);
    const canvas = pageShell.querySelector('.pdf-page-canvas');
    if (!layout || !canvas || !state.pdfJsDoc) {
        return;
    }

    const dpr = window.devicePixelRatio || 1;
    const wanted = Math.max(1, zoom) * dpr;
    const resolution = Math.min(wanted, ZOOM_MAX_BACKING_WIDTH / Math.max(1, layout.screenWidth));
    pageShell.dataset.renderedZoom = targetKey;

    try {
        const pdfPage = await state.pdfJsDoc.getPage(pageNumber);
        if (generation !== state.renderGeneration) {
            return;
        }
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: layout.screenWidth / baseViewport.width });
        const offscreen = document.createElement('canvas');
        offscreen.width = Math.max(1, Math.floor(viewport.width * resolution));
        offscreen.height = Math.max(1, Math.floor(viewport.height * resolution));
        const offscreenContext = offscreen.getContext('2d', { alpha: false });
        await pdfPage.render({
            intent: 'print',
            canvasContext: offscreenContext,
            viewport,
            transform: [resolution, 0, 0, resolution, 0, 0],
        }).promise;
        if (generation !== state.renderGeneration || !pageShell.isConnected) {
            return;
        }
        canvas.width = offscreen.width;
        canvas.height = offscreen.height;
        canvas.getContext('2d', { alpha: false }).drawImage(offscreen, 0, 0);
        if (pageShell.dataset.state === 'released') {
            pageShell.dataset.state = 'ready';
        }
    } catch (error) {
        delete pageShell.dataset.renderedZoom;
    }
}

export function syncDragToolTouchAction(tool) {
    if (!canvasAreaEl) {
        return;
    }
    canvasAreaEl.classList.toggle('is-drag-tool', DRAG_TOOLS.has(tool));
}

export function applyToolDefaultColor(tool) {
    if (state.colorTouched || !colorInput) {
        return;
    }
    const defaultColor = toolDefaultColors[tool];
    if (defaultColor) {
        colorInput.value = defaultColor;
    }
}

export function getPagePoint(event, pageShell) {
    const rect = pageShell.getBoundingClientRect();
    const zoom = getZoom();
    const width = rect.width / zoom;
    const height = rect.height / zoom;
    const x = clamp((event.clientX - rect.left) / zoom, 0, width);
    const y = clamp((event.clientY - rect.top) / zoom, 0, height);
    return { x, y, width, height };
}

export function setActiveThumb(pageNumber) {
    const thumbs = thumbsEl.querySelectorAll('.pdf-thumb-item');
    thumbs.forEach((thumb) => {
        thumb.classList.toggle('active', Number(thumb.dataset.page) === pageNumber);
    });
}

export function createThumbPlaceholder(pageNumber, baseViewport) {
    const scale = THUMB_WIDTH / baseViewport.width;
    const wrapper = document.createElement('button');
    wrapper.type = 'button';
    wrapper.className = 'pdf-thumb-item pdf-thumb-item--pending';
    wrapper.dataset.page = String(pageNumber);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(THUMB_WIDTH));
    canvas.height = Math.max(1, Math.floor(baseViewport.height * scale));

    const label = document.createElement('p');
    label.className = 'pdf-thumb-label';
    label.textContent = `Page ${pageNumber}`;

    wrapper.appendChild(canvas);
    wrapper.appendChild(label);
    wrapper.addEventListener('click', () => {
        scrollToPage(pageNumber);
    });

    thumbsEl.appendChild(wrapper);
    return wrapper;
}

export async function renderThumbnailInto(pdfPage, pageNumber) {
    const wrapper = thumbsEl.querySelector(`.pdf-thumb-item[data-page="${pageNumber}"]`);
    const canvas = wrapper ? wrapper.querySelector('canvas') : null;
    if (!wrapper || !canvas) {
        return;
    }
    const viewport = pdfPage.getViewport({ scale: 1 });
    const thumbViewport = pdfPage.getViewport({ scale: THUMB_WIDTH / viewport.width });
    canvas.width = Math.max(1, Math.floor(thumbViewport.width));
    canvas.height = Math.max(1, Math.floor(thumbViewport.height));
    const context = canvas.getContext('2d', { alpha: false });
    await pdfPage.render({
        intent: 'print',
        canvasContext: context,
        viewport: thumbViewport,
    }).promise;

    try {
        const previewDataUrl = canvas.toDataURL('image/png');
        if (previewDataUrl) {
            state.pageThumbPreviewUrls.set(pageNumber, previewDataUrl);
        }
    } catch (error) {
        // Ignore preview snapshot errors and keep thumbnail rendering.
    }
    wrapper.classList.remove('pdf-thumb-item--pending');
}

export function scrollToPage(pageNumber) {
    const pageShell = pagesEl.querySelector(`[data-page="${pageNumber}"]`);
    if (!pageShell) {
        return;
    }
    // Instant, not smooth: a typed page number is a jump, and smooth
    // scrolling never completes in a background tab.
    pageShell.scrollIntoView({ behavior: 'auto', block: 'start' });
    state.lastActivePage = pageNumber;
    setActiveThumb(pageNumber);
    syncPageIndicator(pageNumber);
    ensurePagePrepared(pageNumber).catch(() => {});
    scheduleViewportWork();
}

export function isPagePrepared(pageNumber) {
    return state.pagesReady.has(Number(pageNumber));
}

export function ensurePagePrepared(pageNumber) {
    return preparePage(Number(pageNumber), state.renderGeneration);
}

export function preparePage(pageNumber, generation) {
    if (!state.pdfJsDoc || generation !== state.renderGeneration) {
        return Promise.resolve();
    }
    if (state.pagePrepare.has(pageNumber)) {
        return state.pagePrepare.get(pageNumber);
    }
    const task = preparePageInner(pageNumber, generation).catch((error) => {
        // Let a later scroll retry a page whose render failed — but say so,
        // and keep the background queue from re-picking it every idle tick:
        // a silent catch here hid a pdf.js upgrade regression and spun the
        // queue on the failing page hundreds of times a minute (Sep 2026).
        console.warn(`PDF editor: preparing page ${pageNumber} failed`, error);
        if (generation === state.renderGeneration) {
            state.pagePrepare.delete(pageNumber);
            state.pagePrepareFailed.add(pageNumber);
        }
    });
    state.pagePrepare.set(pageNumber, task);
    return task;
}

export async function preparePageInner(pageNumber, generation) {
    const pageShell = pagesEl.querySelector(`[data-page="${pageNumber}"]`);
    const layout = state.pageLayouts.get(pageNumber);
    if (!pageShell || !layout) {
        return;
    }
    const pdfPage = await state.pdfJsDoc.getPage(pageNumber);
    if (generation !== state.renderGeneration) {
        return;
    }
    const baseViewport = pdfPage.getViewport({ scale: 1 });
    const scale = layout.screenWidth / baseViewport.width;
    const viewport = pdfPage.getViewport({ scale });
    const canvas = pageShell.querySelector('.pdf-page-canvas');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);
    const context = canvas.getContext('2d', { alpha: false });
    await pdfPage.render({
        intent: 'print',
        canvasContext: context,
        viewport,
        transform: [dpr, 0, 0, dpr, 0, 0],
    }).promise;
    if (generation !== state.renderGeneration) {
        return;
    }

    const textFragments = enrichTextFragmentsFromCanvas(
        await extractPageTextFragments(pdfPage, viewport, scale, pageNumber),
        context,
        canvas,
        dpr
    );
    const formFields = await extractPageFormFields(pdfPage, viewport, pageNumber);
    if (generation !== state.renderGeneration) {
        return;
    }
    state.pageTextFragments.set(pageNumber, textFragments);
    state.pageFormFields.set(pageNumber, formFields);

    pageShell.dataset.renderedZoom = zoomRenderKey(1);
    pageShell.dataset.state = 'ready';
    pageShell.classList.remove('pdf-page-shell--pending');
    state.pagesReady.add(pageNumber);
    redrawPage(pageNumber);
    updateFormFillSummary();

    await renderThumbnailInto(pdfPage, pageNumber);
    if (generation !== state.renderGeneration) {
        return;
    }

    if (getZoom() > 1 && isShellNearViewport(pageShell)) {
        await rerenderPageCanvas(pageShell, getZoom(), generation);
    }

    if (state.pagesReady.size >= state.pdfJsDoc.numPages) {
        onAllPagesPrepared(generation);
    }
}

export function getCurrentViewportPage() {
    const shells = getPageShells();
    if (!shells.length) {
        return 1;
    }
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const center = viewportHeight / 2;
    let best = Number(shells[0].dataset.page) || 1;
    let bestDistance = Infinity;
    for (const shell of shells) {
        const rect = shell.getBoundingClientRect();
        if (rect.top <= center && rect.bottom >= center) {
            return Number(shell.dataset.page) || best;
        }
        const distance = rect.top > center ? rect.top - center : center - rect.bottom;
        if (distance < bestDistance) {
            bestDistance = distance;
            best = Number(shell.dataset.page) || best;
        }
    }
    return best;
}

export function pickNextPageToPrepare() {
    if (!state.pdfJsDoc) {
        return null;
    }
    const pageCount = state.pdfJsDoc.numPages;
    const center = Math.max(1, Math.min(pageCount, state.lastActivePage || 1));
    for (let distance = 0; distance < pageCount; distance++) {
        const candidates = distance === 0 ? [center] : [center + distance, center - distance];
        for (const candidate of candidates) {
            if (candidate >= 1 && candidate <= pageCount && !state.pagePrepare.has(candidate) && !state.pagePrepareFailed.has(candidate)) {
                return candidate;
            }
        }
    }
    return null;
}

export function idleBreath() {
    return new Promise((resolve) => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => resolve(), { timeout: 250 });
        } else {
            window.setTimeout(resolve, 16);
        }
    });
}

export async function ensureAllPagesPrepared(options = {}) {
    if (!state.pdfJsDoc) {
        return;
    }
    const generation = state.renderGeneration;
    const pageCount = state.pdfJsDoc.numPages;
    const { announce = false } = options;
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
        if (generation !== state.renderGeneration) {
            return;
        }
        if (!state.pagesReady.has(pageNumber)) {
            if (announce) {
                setStatus(`Preparing page ${pageNumber} of ${pageCount}...`);
            }
            await preparePage(pageNumber, generation);
        }
    }
}

export function onAllPagesPrepared(generation) {
    if (generation !== state.renderGeneration || state.allPagesPreparedGeneration === generation) {
        return;
    }
    state.allPagesPreparedGeneration = generation;

    // Fill & Sign is the hero flow: a document with real form fields opens in
    // Form Fill unless the person has already picked a tool.
    if (!state.userChoseTool && state.activeSuiteTool === 'edit' && state.activeTool === 'pointer' && countNativeFormFields() > 0) {
        activateTool('form-fill');
    }

    // Overrides that match what the file already holds are noise; drop
    // them now that every page's fields are known.
    const loadedFormValues = new Map();
    addFormFieldsToLoadedValuesMap(loadedFormValues);
    for (const [fieldName, fieldValue] of Array.from(state.formFieldOverrides.entries())) {
        if (!loadedFormValues.has(fieldName)) {
            state.formFieldOverrides.delete(fieldName);
            continue;
        }
        if (isFormFieldOverrideEqualToLoaded(fieldValue, loadedFormValues.get(fieldName))) {
            state.formFieldOverrides.delete(fieldName);
        }
    }
    updateFormFillSummary();
}

export function releaseFarPageCanvases() {
    if (!state.pdfJsDoc || state.pdfJsDoc.numPages < VIRTUALIZE_MIN_PAGES) {
        return;
    }
    // Keep a window of pages around the current one; a distance in pages
    // (not pixels) keeps the window sane on short phone viewports.
    const current = state.lastActivePage || getCurrentViewportPage();
    getPageShells().forEach((pageShell) => {
        if (pageShell.dataset.state !== 'ready') {
            return;
        }
        const pageNumber = Number(pageShell.dataset.page) || 0;
        if (Math.abs(pageNumber - current) > 6 && !isShellNearViewport(pageShell, 600)) {
            const canvas = pageShell.querySelector('.pdf-page-canvas');
            if (canvas) {
                canvas.width = 1;
                canvas.height = 1;
            }
            pageShell.dataset.state = 'released';
            delete pageShell.dataset.renderedZoom;
        }
    });
}

export function materializeNearPages() {
    const generation = state.renderGeneration;
    getPageShells().forEach((pageShell) => {
        if (!isShellNearViewport(pageShell, 600)) {
            return;
        }
        const pageNumber = Number(pageShell.dataset.page);
        if (!state.pagesReady.has(pageNumber)) {
            preparePage(pageNumber, generation);
        } else if (pageShell.dataset.state === 'released') {
            rerenderPageCanvas(pageShell, getZoom(), generation).catch(() => {});
        }
    });
}

export function syncPageIndicator(pageNumber = null) {
    if (!pageInputEl || !state.pdfJsDoc) {
        return;
    }
    const current = pageNumber || state.lastActivePage || 1;
    if (document.activeElement !== pageInputEl) {
        pageInputEl.value = String(current);
    }
    if (pageTotalEl) {
        pageTotalEl.textContent = String(state.pdfJsDoc.numPages);
    }
}

export function scheduleViewportWork() {
    window.clearTimeout(state.viewportWorkTimer);
    state.viewportWorkTimer = window.setTimeout(() => {
        if (!state.pdfJsDoc) {
            return;
        }
        const current = getCurrentViewportPage();
        if (current !== state.lastActivePage) {
            state.lastActivePage = current;
            setActiveThumb(current);
        }
        syncPageIndicator(current);
        materializeNearPages();
        releaseFarPageCanvases();
        rerenderPagesForZoom().catch(() => {});
    }, 120);
}

/** Page count of raw PDF bytes, without rendering anything. 0 when unreadable. */
export async function countPdfPages(bytes) {
    try {
        const task = pdfjsLib.getDocument({ data: new Uint8Array(bytes).slice(0), isEvalSupported: false });
        const doc = await task.promise;
        const count = Number(doc.numPages || 0);
        await doc.destroy();
        return count;
    } catch (error) {
        return 0;
    }
}

export async function renderAllPages(options = {}) {
    commitInlineFormFieldEditor(true);
    const baseBytes = await ensureBasePdfBytesValid();

    // Serialize concurrent render requests (e.g. boot load racing an
    // upload): only the newest generation may touch the page DOM.
    const generation = ++state.renderGeneration;

    hideUploadEmptyState();
    loadingEl.textContent = 'Loading document pages...';
    loadingEl.classList.remove('hidden');
    try {
        await renderAllPagesInner(baseBytes, generation, options);
    } finally {
        if (generation === state.renderGeneration) {
            loadingEl.classList.add('hidden');
        }
    }
}

export async function renderAllPagesInner(baseBytes, generation, options = {}) {
    pagesEl.innerHTML = '';
    thumbsEl.innerHTML = '';
    pagesEl.style.setProperty('--pdf-zoom', '1');
    state.zoom = 1;
    state.pageLayouts.clear();
    state.pageTextFragments.clear();
    state.pageTextBlocks.clear();
    state.pageFormFields.clear();
    state.pageThumbPreviewUrls.clear();
    state.pagePrepare = new Map();
    state.pagePrepareFailed = new Set();
    state.pagesReady = new Set();
    state.prepareQueueRunning = false;

    // Pass a copy: pdf.js transfers the buffer to its worker and detaches
    // the original, which would corrupt state.basePdfBytes for later saves.
    const loadingTask = pdfjsLib.getDocument({ data: baseBytes.slice() });
    const pdfJsDoc = await loadingTask.promise;
    if (generation !== state.renderGeneration) {
        return;
    }
    state.pdfJsDoc = pdfJsDoc;
    syncDocumentRequiredActionsDisabledState();

    const pageCount = state.pdfJsDoc.numPages;
    const fitWidth = computeFitRenderWidth();
    // A draft restore may ask for the width its coordinates were captured
    // at; the zoom then fits that width on screen.
    const forcedWidth = Number(options.targetWidth) || 0;
    const targetWidth = forcedWidth > 0 ? forcedWidth : fitWidth;
    state.baseRenderWidth = targetWidth;

    // Pass 1: size every page and thumbnail so the document has its final
    // shape immediately. No pixels are drawn yet.
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
        if (generation !== state.renderGeneration) {
            return;
        }
        const pdfPage = await state.pdfJsDoc.getPage(pageNumber);
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const scale = targetWidth / baseViewport.width;
        const viewport = pdfPage.getViewport({ scale });

        const pageShell = createPageShell(pageNumber);
        pageShell.classList.add('pdf-page-shell--pending');
        pageShell.dataset.state = 'pending';
        const canvas = pageShell.querySelector('.pdf-page-canvas');
        const svg = pageShell.querySelector('.pdf-annotation-layer');
        canvas.width = 1;
        canvas.height = 1;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        svg.style.width = `${viewport.width}px`;
        svg.style.height = `${viewport.height}px`;

        state.pageLayouts.set(pageNumber, {
            screenWidth: viewport.width,
            screenHeight: viewport.height,
            pdfWidth: Math.abs(pdfPage.view[2] - pdfPage.view[0]),
            pdfHeight: Math.abs(pdfPage.view[3] - pdfPage.view[1]),
        });
        pagesEl.appendChild(pageShell);
        createThumbPlaceholder(pageNumber, baseViewport);
    }

    if (generation !== state.renderGeneration) {
        return;
    }

    redrawAllPages();
    const activePage = Math.max(1, Math.min(state.lastActivePage || 1, pageCount));
    state.lastActivePage = activePage;
    setActiveThumb(activePage);
    syncPageIndicator(activePage);
    updateMergeInsertOptions(state.merge.insertAfter);
    syncSplitExtractSummary();
    syncDeletePagesSummary();
    refreshPageCursor();
    updateFormFillSummary();
    updateUndoRedoButtons();
    syncDragToolTouchAction(state.activeTool);

    // Fit the rendered width on screen (only differs from 1 when a draft
    // forced its own render width).
    state.zoomMode = 'fit';
    applyZoom(getFitZoom(), { mode: 'fit' });
    syncZoomUi();

    // Pass 2: the pages on screen, then everything else in the background.
    const nearShells = getPageShells().filter((shell) => isShellNearViewport(shell, 600));
    const nearPages = nearShells.length
        ? nearShells.map((shell) => Number(shell.dataset.page))
        : [activePage];
    for (const pageNumber of nearPages) {
        if (generation !== state.renderGeneration) {
            return;
        }
        loadingEl.textContent = pageCount > 1 ? `Loading page ${pageNumber} of ${pageCount}...` : 'Loading document pages...';
        await preparePage(pageNumber, generation);
    }
    if (generation !== state.renderGeneration) {
        return;
    }
    setStatus(`${pageCount} page${pageCount === 1 ? '' : 's'} loaded`);
    runBackgroundPreparation(generation).catch(() => {});

    if (state.localDraft.pendingRestore) {
        const pendingDraft = state.localDraft.pendingRestore;
        state.localDraft.pendingRestore = null;
        applyDraftToState(pendingDraft);
    }

    const bytesForKey = state.basePdfBytes;
    computeDocumentKey(bytesForKey).then((key) => {
        if (generation !== state.renderGeneration) {
            return;
        }
        state.localDraft.key = key;
        offerDraftRestore(key).catch(() => {});
    }).catch(() => {});
}
